/**
 * 工具箱服务
 * 处理工具箱相关的 API 调用
 * 使用统一的 userApiClient 进行 API 调用
 *
 * @see Feature: toolbox-template-i18n
 */

import { userApiClient } from "@/lib/userApiClient";
import type { RegulatoryTier } from "@/types/toolbox-template";

// ============ 类型定义 ============

// 从 toolbox-template.ts 导入 RegulatoryTier 类型
export type { RegulatoryTier } from "@/types/toolbox-template";

/** 工具箱模板搜索结果 */
export interface ToolboxTemplateSearchResult {
  id: number;
  name: string;
  name_all: Record<string, string>;
  category_id: number;
  category_name: string;
  category_slug: string;
  icon?: string;
  slug: string;
  match_type: "name" | "keyword";
  matched_keyword?: string | null;
  // 合规字段
  regulatory_tier?: RegulatoryTier;
  compliance_warning?: string | null;
  is_consumable?: boolean;
  keywords?: Record<string, string[]>;
}

/** 推荐模板 */
export interface RecommendedTemplate {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
  category_slug: string;
  icon?: string;
  regulatory_tier?: RegulatoryTier;
  is_consumable?: boolean;
}

/** 工具箱分类 */
export interface ToolboxCategory {
  id: number;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
}

/** 工具箱物品 */
export interface ToolboxItem {
  id: number;
  item_type: string;
  name: string;
  display_name: string;
  created_at: string;
  toolbox_template_id?: number;
  use_template_name: boolean;
  template_name?: string;
  display_icon?: string;
  thumbnail_url?: string | null;
  // 合规字段
  regulatory_tier?: RegulatoryTier;
  compliance_warning?: string | null;
  is_consumable?: boolean;
  toolbox_template?: {
    id: number;
    slug: string;
    name: string;
    name_all: Record<string, string>;
    category_id: number;
    category_slug: string;
    icon?: string;
    regulatory_tier?: RegulatoryTier;
    compliance_warning?: string | null;
    is_consumable?: boolean;
  };
  // 有效期字段
  expire_at?: string | null;
  opened_at?: string | null;
  status?: "good" | "expiring_soon" | "expired";
  // 商品预设详情
  brand?: string | null;
  model?: string | null;
  concentration_ratio?: number;
  effective_concentration_ratio?: number;
  // 库存相关
  quantity?: number | null;
  unit?: string | null;
  global_product_id?: number | null;
}

/** 分组的工具箱物品 */
export type GroupedToolboxItems = Record<string, ToolboxItem[]>;

/** 创建工具箱物品请求 */
export interface CreateToolboxItemRequest {
  item_type: string;
  name: string;
  toolbox_template_id?: number | null;
  use_template_name?: boolean;
  regulatory_acknowledged?: boolean;
  // 有效期字段
  expire_at?: string | null;
  opened_at?: string | null;
  // 浓度字段
  concentration_ratio?: number;
  concentration_input_mode?: string;
}

/** 自定义物品合规检查结果 */
export interface CustomItemComplianceCheck {
  has_match: boolean;
  matched_template?: {
    id: number;
    name: string;
    regulatory_tier: RegulatoryTier;
    compliance_warning?: string | null;
  } | null;
}

/** 商品预设市场区域 */
export type GlobalProductMarket = "global" | "cn" | "us" | "jp" | "eu";

/** 商品预设搜索结果 */
export interface GlobalProductSearchResult {
  id: number;
  type: "product";
  name: string;
  brand: string;
  model: string;
  barcode?: string | null;
  template_id: number;
  template_name?: string;
  thumbnail_url?: string | null;
  usage_count: number;
  sort_order: number;
  regulatory_tier?: RegulatoryTier;
  product_key?: string;
  market: GlobalProductMarket;
}

/** 商品预设详情 */
export interface GlobalProductDetail extends GlobalProductSearchResult {
  name_i18n: Record<string, string>;
  specifications: Record<string, string>;
  image_url?: string | null;
  template: {
    id: number;
    name: string;
    product_key?: string;
    regulatory_tier?: RegulatoryTier;
    compliance_warning?: string | null;
    is_consumable?: boolean;
  };
  created_at: string;
  updated_at: string;
}

/** 混合搜索结果 */
export interface MixedSearchResult {
  templates: ToolboxTemplateSearchResult[];
  products: GlobalProductSearchResult[];
}

/** 基于商品创建工具箱物品请求 */
export interface CreateFromProductRequest {
  source_type: "product";
  source_id: number;
  name?: string;
  regulatory_acknowledged?: boolean;
  concentration_ratio?: number;
  concentration_input_mode?: string;
}

// ============ 辅助函数 ============

/**
 * 获取当前语言
 * 优先从 localStorage 读取，回退到 document.documentElement.lang
 */
function getCurrentLocale(): string {
  if (typeof window !== "undefined") {
    return (
      localStorage.getItem("i18nextLng") ||
      document.documentElement.lang ||
      "zh"
    );
  }
  return "zh";
}

// ============ 服务类 ============

/**
 * 工具箱服务类
 * 提供工具箱相关的 API 操作
 */
class ToolboxService {
  /**
   * 搜索工具箱模板
   * @param query 搜索关键词
   * @param _signal AbortSignal 用于取消请求（暂不支持）
   * @returns 匹配的模板列表
   */
  async searchTemplates(
    query: string,
    _signal?: AbortSignal,
  ): Promise<ToolboxTemplateSearchResult[]> {
    if (!query.trim()) {
      return [];
    }
    // userApiClient 已自动解包 data 字段，直接返回结果
    return userApiClient.get<ToolboxTemplateSearchResult[]>(
      "/toolbox/templates/search",
      {
        params: { q: query.trim(), locale: getCurrentLocale() },
      },
    );
  }

  /**
   * 获取推荐的工具箱模板
   * @returns 推荐模板列表
   */
  async getRecommendedTemplates(): Promise<RecommendedTemplate[]> {
    // userApiClient 已自动解包 data 字段，直接返回结果
    return userApiClient.get<RecommendedTemplate[]>(
      "/toolbox/templates/recommended",
      { params: { locale: getCurrentLocale() } },
    );
  }

  /**
   * 获取工具箱分类列表
   * @returns 分类列表
   */
  async getCategories(): Promise<ToolboxCategory[]> {
    // userApiClient 已自动解包 data 字段，直接返回结果
    return userApiClient.get<ToolboxCategory[]>("/toolbox/categories", {
      params: { locale: getCurrentLocale() },
    });
  }

  /**
   * 获取当前用户的工具箱物品（按分类分组）
   * @returns 分组的工具箱物品
   */
  async getToolboxItems(): Promise<GroupedToolboxItems> {
    // userApiClient 已自动解包 data 字段，直接返回结果
    return userApiClient.get<GroupedToolboxItems>("/toolbox", {
      params: { locale: getCurrentLocale() },
    });
  }

  /**
   * 创建工具箱物品
   * @param data 创建数据
   * @returns 创建的物品
   */
  async createItem(data: CreateToolboxItemRequest): Promise<ToolboxItem> {
    // userApiClient 已自动解包 data 字段，直接返回结果
    return userApiClient.post<ToolboxItem>("/toolbox", data, {
      params: { locale: getCurrentLocale() },
    });
  }

  /**
   * 更新工具箱物品
   * @param id 物品 ID
   * @param data 更新数据
   * @returns 更新后的物品
   */
  async updateItem(
    id: number,
    data: Partial<CreateToolboxItemRequest>,
  ): Promise<ToolboxItem> {
    // userApiClient 已自动解包 data 字段，直接返回结果
    return userApiClient.patch<ToolboxItem>(`/toolbox/${id}`, data, {
      params: { locale: getCurrentLocale() },
    });
  }

  /**
   * 删除工具箱物品
   * @param id 物品 ID
   */
  async deleteItem(id: number): Promise<void> {
    await userApiClient.delete(`/toolbox/${id}`, {
      params: { locale: getCurrentLocale() },
      // 禁用重试和去重，因为 DELETE 是幂等操作，但重复请求会导致 404
      enableRetry: false,
      enableDeduplication: false,
    });
  }

  // ============ 商品预设相关 API ============

  /**
   * 混合搜索（同时搜索模板和商品预设）
   * @param query 搜索关键词
   * @param options 搜索选项
   * @returns 混合搜索结果
   */
  async mixedSearch(
    query: string,
    options?: {
      market?: GlobalProductMarket;
      regulatory_tier?: RegulatoryTier;
      limit?: number;
    },
  ): Promise<MixedSearchResult> {
    return userApiClient.get<MixedSearchResult>("/toolbox/search", {
      params: {
        q: query.trim(),
        locale: getCurrentLocale(),
        ...options,
      },
    });
  }

  /**
   * 获取商品预设详情
   * @param id 商品预设 ID
   * @returns 商品预设详情
   */
  async getGlobalProduct(id: number): Promise<GlobalProductDetail> {
    return userApiClient.get<GlobalProductDetail>(`/global_products/${id}`, {
      params: { locale: getCurrentLocale() },
    });
  }

  /**
   * 获取商品预设列表
   * @param options 过滤选项
   * @returns 商品预设列表
   */
  async getGlobalProducts(options?: {
    market?: GlobalProductMarket;
    toolbox_template_id?: number;
    limit?: number;
  }): Promise<GlobalProductSearchResult[]> {
    const response = await userApiClient.get<{
      products: GlobalProductSearchResult[];
    }>("/global_products", {
      params: {
        locale: getCurrentLocale(),
        ...options,
      },
    });
    return response.products;
  }

  /**
   * 基于商品预设创建工具箱物品
   * @param data 创建数据
   * @returns 创建的物品
   */
  async createItemFromProduct(
    data: CreateFromProductRequest,
  ): Promise<ToolboxItem> {
    return userApiClient.post<ToolboxItem>("/toolbox", data, {
      params: { locale: getCurrentLocale() },
    });
  }
}

// 导出单例
export const toolboxService = new ToolboxService();
export default toolboxService;
